import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check } from "lucide-react";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })
    .formatToParts(d).reduce((a, x) => ({ ...a, [x.type]: x.value }), {});
  return `${p.year}-${p.month}-${p.day}T${p.hour === "24" ? "00" : p.hour}:${p.minute}`;
};
// Interpreta un datetime-local como hora Europe/Madrid y devuelve ISO UTC
const localInputToIso = (v) => {
  if (!v) return null;
  const [date, time] = v.split("T");
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const guess = new Date(Date.UTC(y, m - 1, d, hh, mm));
  const asMadrid = new Date(guess.toLocaleString("en-US", { timeZone: "Europe/Madrid" }));
  const asUtc = new Date(guess.toLocaleString("en-US", { timeZone: "UTC" }));
  return new Date(guess.getTime() - (asMadrid.getTime() - asUtc.getTime())).toISOString();
};

const ACCION_POR_MOTIVO = { olvide_salida: "crear", olvide_entrada: "crear", hora_incorrecta: "modificar", duplicado: "eliminar", otro: "modificar" };

export function CorreccionDialog({ open, onClose, fecha, fichaje, onSaved }) {
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ motivo_tipo: "olvide_salida", timestamp: "", tipo: "salida", comentario: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/portal-empleado/correcciones/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!open) return;
    if (fichaje) {
      setForm({ motivo_tipo: "hora_incorrecta", timestamp: toLocalInput(fichaje.timestamp), tipo: fichaje.tipo, comentario: "" });
    } else {
      setForm({ motivo_tipo: "olvide_salida", timestamp: fecha ? `${fecha}T17:00` : "", tipo: "salida", comentario: "" });
    }
  }, [open, fichaje, fecha]);

  const accion = fichaje ? (form.motivo_tipo === "duplicado" ? "eliminar" : "modificar") : "crear";
  const necesitaHora = accion !== "eliminar";

  const submit = async () => {
    if (!form.comentario.trim()) return toast.error("Explica brevemente el motivo");
    if (necesitaHora && !form.timestamp) return toast.error("Indica fecha y hora correctas");
    setSaving(true);
    try {
      await api.post("/fichajes/correcciones", {
        fichaje_id: fichaje?.id || null,
        motivo_tipo: form.motivo_tipo,
        accion,
        timestamp_propuesto: necesitaHora ? localInputToIso(form.timestamp) : null,
        tipo_propuesto: necesitaHora ? form.tipo : null,
        motivo: form.comentario.trim(),
        fecha_laboral: fecha || null,
      });
      toast.success("Solicitud de corrección enviada · administración la revisará");
      onSaved && onSaved();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al enviar"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="dialog-correccion">
        <DialogHeader>
          <DialogTitle>Solicitar corrección de fichaje</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Jornada {fecha}{fichaje ? ` · ${fichaje.tipo} ${toLocalInput(fichaje.timestamp).replace("T", " ")}` : ""}. No se modifica nada hasta que administración apruebe.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Motivo</Label>
            <Select value={form.motivo_tipo} onValueChange={(v) => setForm({ ...form, motivo_tipo: v, tipo: v === "olvide_entrada" ? "entrada" : v === "olvide_salida" ? "salida" : form.tipo })}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="correccion-motivo"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                {(meta?.motivos || Object.keys(ACCION_POR_MOTIVO)).filter((m) => fichaje ? m !== "olvide_salida" && m !== "olvide_entrada" : m !== "duplicado")
                  .map((m) => <SelectItem key={m} value={m} data-testid={`motivo-${m}`}>{meta?.labels?.[m] || m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {necesitaHora && (
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label className="text-xs">{accion === "crear" ? "Hora real del fichaje olvidado" : "Hora correcta"} (Madrid)</Label>
                <Input type="datetime-local" value={form.timestamp} onChange={(e) => setForm({ ...form, timestamp: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="correccion-timestamp" />
              </div>
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="correccion-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    <SelectItem value="entrada">entrada</SelectItem><SelectItem value="salida">salida</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {accion === "eliminar" && <div className="text-xs text-amber-300">Se solicitará ELIMINAR este fichaje por duplicado.</div>}
          <div>
            <Label className="text-xs">Comentario</Label>
            <Textarea value={form.comentario} onChange={(e) => setForm({ ...form, comentario: e.target.value })}
              placeholder="Ej.: salí a las 18:10 pero olvidé fichar" className="bg-zinc-900 border-zinc-800 min-h-[60px]" data-testid="correccion-comentario" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="correccion-submit">
            <Check className="w-4 h-4 mr-2" /> Enviar solicitud
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
